import { useEffect, useMemo, useState } from 'react';
import {
    Box, Grid, Paper, Typography, Chip, Button, CircularProgress, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
    Accordion, AccordionSummary, AccordionDetails, TextField, Stack, Rating, Link,
    ToggleButton, ToggleButtonGroup, Avatar, FormControlLabel, Switch, IconButton,
} from '@mui/material';
import {
    WarningAmber as WarningIcon,
    TrendingDown as TrendingDownIcon,
    Bolt as BoltIcon,
    ReviewsOutlined as ReviewsIcon,
    ExpandMore as ExpandMoreIcon,
    PhotoCamera as PhotoIcon,
    Videocam as VideoIcon,
    OpenInNew as OpenInNewIcon,
    Refresh as RefreshIcon,
    CheckCircleOutline as CheckIcon,
    Undo as UndoIcon,
    DoneAll as DoneAllIcon,
    Tune as TuneIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import PageHeader from '../../components/ui/PageHeader';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';
import StatCard from '../../components/ui/StatCard';
import { getWbImageUrl, toThumbnail } from '../../utils/productImage';

// Фото товара: сначала image_url из БД (cards), иначе WB-CDN по nmID.
const productImg = (row) => (row?.image_url ? toThumbnail(row.image_url) : getWbImageUrl(row?.nm_id));

// сигналы -> русские подписи + цвета
const SIGNALS = {
    impact: { label: 'Падение', color: 'error', hint: 'Официальный рейтинг WB заметно упал за последнее время (impact-окно)' },
    velocity: { label: 'Поток плохих', color: 'warning', hint: 'Кластер плохих отзывов: несколько за неделю или несколько в один день' },
    trend: { label: 'Снижение', color: 'info', hint: 'Устойчивое снижение рейтинга за длинный период' },
};

const THRESHOLD_FIELDS = [
    { key: 'impact_min', label: 'Мин. падение за 30 дней', step: 0.1 },
    { key: 'spike_bad_min', label: 'Плохих за 7 дней (поток)', step: 1 },
    { key: 'same_day_bad_min', label: 'Плохих в один день (всплеск)', step: 1 },
    { key: 'trend_min', label: 'Мин. снижение за 90 дней', step: 0.1 },
    { key: 'active_days', label: 'Окно активности (дней с заказами)', step: 1 },
];

const wbCardUrl = (nmId) => `https://www.wildberries.ru/catalog/${nmId}/detail.aspx`;

function fmt(v, dec = 2) {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('ru-RU', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

export default function ReviewsAlarm() {
    const { error: showError, success: showSuccess } = useNotify();

    const [filters, setFilters] = useState({ mp: '1' });
    const [thresholds, setThresholds] = useState({});
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ kpi: null, products: [], freshBad: [], thresholds: {} });
    const [view, setView] = useState('products'); // 'products' | 'feed'
    const [includeProcessed, setIncludeProcessed] = useState(false);
    const [busyKeys, setBusyKeys] = useState({}); // nm_id|org -> true пока идёт ack/unack

    const load = async () => {
        setLoading(true);
        try {
            const params = { ...buildFilterParams({ ...filters, mp: '1' }), ...thresholds };
            delete params.mp; // роут WB-only
            if (includeProcessed) params.include_processed = '1';
            const { data: resp } = await axios.get('/analytics/wb/reviews/alarm', { params });
            setData(resp);
            // подтянуть эффективные пороги с бэка (после clamp), чтобы поля показывали реальные значения
            if (resp.thresholds && Object.keys(thresholds).length === 0) {
                const t = {};
                THRESHOLD_FIELDS.forEach((f) => { t[f.key] = resp.thresholds[f.key]; });
                setThresholds(t);
            }
        } catch (e) {
            showError('Не удалось загрузить данные по отзывам');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, []);
    // перезагружаем при переключении «показать обработанные»
    useEffect(() => { load(); /* eslint-disable-line */ }, [includeProcessed]);

    const setBusy = (key, val) => setBusyKeys((m) => ({ ...m, [key]: val }));

    const handleAck = async (p) => {
        const key = `${p.nm_id}|${p.organization}`;
        setBusy(key, true);
        try {
            await axios.post('/analytics/wb/reviews/alarm/ack', {
                nm_id: p.nm_id,
                organization: p.organization,
                rating_now: p.rating_now,
            });
            showSuccess('Отмечено как обработанное');
            await load();
        } catch (e) {
            showError('Не удалось отметить');
        } finally {
            setBusy(key, false);
        }
    };

    const handleUnack = async (p) => {
        const key = `${p.nm_id}|${p.organization}`;
        setBusy(key, true);
        try {
            await axios.delete('/analytics/wb/reviews/alarm/ack', {
                data: { nm_id: p.nm_id, organization: p.organization },
            });
            showSuccess('Отметка снята');
            await load();
        } catch (e) {
            showError('Не удалось снять отметку');
        } finally {
            setBusy(key, false);
        }
    };

    const kpi = data.kpi;
    const products = data.products || [];
    const freshBad = data.freshBad || [];

    const feedFiltered = useMemo(() => freshBad, [freshBad]);

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
            <PageHeader
                title="Уведомления по отзывам и рейтингу"
                subtitle="Только то, что реально влияет: обвал рейтинга, поток плохих отзывов, устойчивое снижение (Wildberries)"
            />

            {/* Фильтры */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <ProductFilters
                    value={filters}
                    onChange={setFilters}
                    showMp={false}
                    defaultMp="1"
                    showArticle
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={load} startIcon={<RefreshIcon />} disabled={loading}>
                        Обновить
                    </Button>
                    <FormControlLabel
                        control={<Switch checked={includeProcessed} onChange={(e) => setIncludeProcessed(e.target.checked)} size="small" />}
                        label="Показывать обработанные"
                    />
                </Box>

                {/* Пороги */}
                <Accordion
                    sx={{
                        mt: 2,
                        boxShadow: 'none',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { m: 0, mt: 2 },
                    }}
                    disableGutters
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon color="primary" sx={{ fontSize: 18 }} />}
                        sx={{
                            px: 1.5,
                            minHeight: 36,
                            borderRadius: 2,
                            bgcolor: 'action.hover',
                            '&:hover': { bgcolor: 'action.selected' },
                            '&.Mui-expanded': { minHeight: 36 },
                            '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 0.75, my: 0.5 },
                            '& .MuiAccordionSummary-content.Mui-expanded': { my: 0.5 },
                        }}
                    >
                        <TuneIcon sx={{ fontSize: 16 }} color="primary" />
                        <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                            Пороги чувствительности
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            — нажмите, чтобы настроить
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pt: 2 }}>
                        <Grid container spacing={2}>
                            {THRESHOLD_FIELDS.map((f) => (
                                <Grid item xs={6} md={2.4} key={f.key}>
                                    <TextField
                                        label={f.label}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        inputProps={{ step: f.step }}
                                        value={thresholds[f.key] ?? ''}
                                        onChange={(e) => setThresholds((t) => ({ ...t, [f.key]: e.target.value }))}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Рейтинг — официальный из WB (отчёт Джем), шаг 0.1. «Падение» и «снижение» считаются по реальному рейтингу WB за 30 и 90 дней. «Поток плохих» — по свежим отзывам из WB.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            </Paper>

            {/* KPI */}
            {kpi && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} md={2.4}>
                        <StatCard title={includeProcessed ? 'Товаров в аларме' : 'Новых в аларме'} value={kpi.alarms_total} icon={<WarningIcon />} color="error"
                            subtitle={kpi.processed_count ? `обработано: ${kpi.processed_count}` : `из ${kpi.products_total} активных с отзывами`} />
                    </Grid>
                    <Grid item xs={6} md={2.4}>
                        <StatCard title="Обвал рейтинга" value={kpi.impact_count} icon={<TrendingDownIcon />} color="error" />
                    </Grid>
                    <Grid item xs={6} md={2.4}>
                        <StatCard title="Поток плохих" value={kpi.velocity_count} icon={<BoltIcon />} color="warning" />
                    </Grid>
                    <Grid item xs={6} md={2.4}>
                        <StatCard title="Устойчивое снижение" value={kpi.trend_count} icon={<TrendingDownIcon />} color="info" />
                    </Grid>
                    <Grid item xs={6} md={2.4}>
                        <StatCard title="Плохих без ответа" value={kpi.fresh_bad_unanswered} icon={<ReviewsIcon />} color="secondary"
                            subtitle={`всего свежих плохих: ${kpi.fresh_bad_total}`} />
                    </Grid>
                </Grid>
            )}

            <ToggleButtonGroup
                value={view}
                exclusive
                size="small"
                onChange={(e, v) => v && setView(v)}
                sx={{ mb: 2 }}
            >
                <ToggleButton value="products">Проблемные товары ({products.length})</ToggleButton>
                <ToggleButton value="feed">Лента плохих отзывов ({freshBad.length})</ToggleButton>
            </ToggleButtonGroup>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            )}

            {!loading && view === 'products' && (
                <ProductsTable products={products} onAck={handleAck} onUnack={handleUnack} busyKeys={busyKeys} />
            )}

            {!loading && view === 'feed' && (
                <FeedList items={feedFiltered} />
            )}
        </Box>
    );
}

function SignalChips({ signals }) {
    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(signals || []).map((s) => {
                const cfg = SIGNALS[s];
                if (!cfg) return null;
                return (
                    <Tooltip title={cfg.hint} key={s}>
                        <Chip label={cfg.label} color={cfg.color} size="small" variant="filled" />
                    </Tooltip>
                );
            })}
        </Stack>
    );
}

const cellSx = { py: 0.75, px: 1, fontSize: '0.82rem' };
const headSx = { ...cellSx, fontWeight: 700, bgcolor: 'grey.100', whiteSpace: 'nowrap' };

function ProductsTable({ products, onAck, onUnack, busyKeys = {} }) {
    if (!products.length) {
        return <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>Проблемных товаров нет — всё спокойно 👌</Paper>;
    }
    return (
        <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={headSx}>Товар</TableCell>
                        <TableCell sx={headSx} align="center">Рейтинг сейчас</TableCell>
                        <TableCell sx={headSx} align="center">Падение (30д)</TableCell>
                        <TableCell sx={headSx} align="center">Снижение (90д)</TableCell>
                        <TableCell sx={headSx} align="center">Плохих 7д / в день</TableCell>
                        <TableCell sx={headSx} align="center">Отзывов / заказов</TableCell>
                        <TableCell sx={headSx}>Сигналы</TableCell>
                        <TableCell sx={headSx} align="center">Статус</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {products.map((p) => {
                        const key = `${p.nm_id}|${p.organization}`;
                        const busy = !!busyKeys[key];
                        return (
                        <TableRow key={`${p.nm_id}-${p.organization}`} hover
                            sx={p.processed ? { opacity: 0.6, bgcolor: 'action.hover' } : undefined}>
                            <TableCell sx={cellSx}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Avatar src={productImg(p)} variant="rounded" sx={{ width: 44, height: 44 }} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Link href={wbCardUrl(p.nm_id)} target="_blank" rel="noreferrer"
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontWeight: 600 }}>
                                            {p.offer_id || p.nm_id}<OpenInNewIcon sx={{ fontSize: 13 }} />
                                        </Link>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 220 }}>
                                            {p.podgruppa || p.category_name || ''} · {p.organization}
                                        </Typography>
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell sx={cellSx} align="center">
                                <Typography sx={{ fontWeight: 700, color: p.rating_now < 4.8 ? 'error.main' : 'text.primary' }}>
                                    {fmt(p.rating_now, 2)}
                                </Typography>
                                {p.rating_prior != null && p.rating_prior > p.rating_now && (
                                    <Typography variant="caption" color="text.secondary">было {fmt(p.rating_prior, 2)}</Typography>
                                )}
                            </TableCell>
                            <TableCell sx={cellSx} align="center">
                                {p.impact > 0 ? (
                                    <Typography sx={{ color: 'error.main', fontWeight: 600 }}>−{fmt(p.impact, 2)}</Typography>
                                ) : '—'}
                            </TableCell>
                            <TableCell sx={cellSx} align="center">
                                {p.trend_drop > 0 ? <Typography sx={{ color: 'info.main' }}>−{fmt(p.trend_drop, 2)}</Typography> : '—'}
                            </TableCell>
                            <TableCell sx={cellSx} align="center">
                                <b>{p.bad7}</b>{p.max_same_day > 1 ? ` / ${p.max_same_day}` : ''}
                            </TableCell>
                            <TableCell sx={cellSx} align="center">
                                {p.n_total} / {p.orders30}
                            </TableCell>
                            <TableCell sx={cellSx}>
                                <SignalChips signals={p.signals} />
                                {p.recent_bad_unanswered > 0 && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.3 }}>
                                        без ответа: {p.recent_bad_unanswered}
                                    </Typography>
                                )}
                                {p.acked && p.worsened && (
                                    <Tooltip title={`Отмечен при рейтинге ${fmt(p.acked_rating, 2)}, сейчас ниже`}>
                                        <Chip label="ухудшилось после отметки" color="error" size="small" variant="outlined" sx={{ mt: 0.3 }} />
                                    </Tooltip>
                                )}
                                {p.acked && !p.worsened && p.new_bad_since_ack && (
                                    <Tooltip title="После отметки пришли новые плохие отзывы">
                                        <Chip label="новые отзывы после отметки" color="warning" size="small" variant="outlined" sx={{ mt: 0.3 }} />
                                    </Tooltip>
                                )}
                            </TableCell>
                            <TableCell sx={cellSx} align="center">
                                {p.processed ? (
                                    <Stack spacing={0.3} alignItems="center">
                                        <Chip icon={<DoneAllIcon sx={{ fontSize: 15 }} />} label="обработан" color="success" size="small" variant="outlined" />
                                        {p.acked_at && (
                                            <Typography variant="caption" color="text.secondary">{p.acked_at}</Typography>
                                        )}
                                        <Button size="small" startIcon={<UndoIcon />} onClick={() => onUnack(p)} disabled={busy} sx={{ minWidth: 0 }}>
                                            вернуть
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Tooltip title="Отметить обработанным — скрыть из списка, пока не станет хуже">
                                        <span>
                                            <Button size="small" variant="outlined" color="success"
                                                startIcon={<CheckIcon />} onClick={() => onAck(p)} disabled={busy}>
                                                Обработано
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </TableCell>
                        </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function FeedList({ items }) {
    if (!items.length) {
        return <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>Свежих плохих отзывов нет</Paper>;
    }
    return (
        <Stack spacing={1.5}>
            {items.map((r, i) => (
                <Paper key={i} sx={{ p: 1.5, borderLeft: 4, borderColor: r.product_alarm ? 'error.main' : 'grey.300' }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Avatar src={productImg(r)} variant="rounded" sx={{ width: 48, height: 48 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Rating value={r.rating} max={5} readOnly size="small" />
                                <Link href={wbCardUrl(r.nm_id)} target="_blank" rel="noreferrer" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                    {r.offer_id || r.nm_id}
                                </Link>
                                {r.podgruppa && <Chip label={r.podgruppa} size="small" variant="outlined" />}
                                {r.product_rating_now != null && (
                                    <Chip label={`рейтинг ${fmt(r.product_rating_now, 2)}`} size="small"
                                        color={r.product_rating_now < 4.8 ? 'error' : 'default'} variant="outlined" />
                                )}
                                {r.product_alarm && <SignalChips signals={r.product_signals} />}
                                <Box sx={{ flex: 1 }} />
                                <Typography variant="caption" color="text.secondary">{r.created}</Typography>
                            </Box>
                            {r.text && (
                                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{r.text}</Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                {r.user_name && <Typography variant="caption" color="text.secondary">{r.user_name}</Typography>}
                                {r.photo_count > 0 && <Chip icon={<PhotoIcon />} label={r.photo_count} size="small" variant="outlined" />}
                                {r.has_video && <Chip icon={<VideoIcon />} label="видео" size="small" variant="outlined" />}
                                <Chip label={r.is_answered ? 'отвечен' : 'без ответа'} size="small"
                                    color={r.is_answered ? 'success' : 'warning'} variant={r.is_answered ? 'outlined' : 'filled'} />
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            ))}
        </Stack>
    );
}
